import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { getDefaultMapPosition, hasValidCenter } from '../logic/mapFocus';
import useTripMapDayFocus from './useTripMapDayFocus';
import useTripMapFraming from './useTripMapFraming';
import {
  useMapState,
  useMapZoom,
  useResetMapView,
  useSetMapState,
  useSetMapZoom,
  useTripFilters,
} from './useTripStoreSelectors';
import type { TripMapRefs } from './useTripMapDayFocus';

const COUNTRY_ZOOM = 7;
const DEFAULT_ZOOM = 7;
const SAVE_CAMERA_DELAY_MS = 500;

export interface TripMapState {
  center: { lat: number; lng: number };
  zoom: number;
  onLoad: (map: any) => void;
  onUnmount: () => void;
  onZoomChanged: (event: { map: any }) => void;
  onBoundsChanged: () => void;
}

// What the map remembers and where it looks. The camera is saved per trip in
// `mapState`, what the map draws (its zoom) lives
// in the store, and the Google map instance stays here: it cannot be stored.
// The screen calls this hook, not the map view, so a tab that hides the map
// does not drop any of it.
export default function useTripMapState({
  tripId,
  experiences,
  organizationPosition,
}: {
  tripId: string;
  experiences: any[];
  organizationPosition?: any;
}): TripMapState {
  const tripFilters = useTripFilters();
  const mapState = useMapState();
  const setMapState = useSetMapState();
  const mapZoom = useMapZoom();
  const setMapZoom = useSetMapZoom();
  const resetMapView = useResetMapView();

  const [map, setMap] = useState<any>();
  const refs: TripMapRefs = useRef({
    hasUserSelectedDay: { current: false },
    isFirstMount: { current: true },
    initialCenter: { current: mapState.center },
  }).current;
  const initialZoomRef = useRef(mapState?.zoom || DEFAULT_ZOOM);
  const zoom = mapZoom ?? initialZoomRef.current;

  const defaultCenter = useMemo(
    () =>
      getDefaultMapPosition({
        countries: tripFilters?.countries,
        organizationPosition,
      }),
    [tripFilters?.countries, organizationPosition],
  );
  const hasSavedCamera =
    mapState.tripId === tripId && hasValidCenter(mapState.center);

  // The map draws from a blank view each time the screen opens.
  useEffect(() => {
    resetMapView();

    return resetMapView;
  }, [resetMapView]);

  useEffect(() => {
    refs.hasUserSelectedDay.current = false;
    refs.isFirstMount.current = true;
    refs.initialCenter.current = null;
  }, [tripId, refs]);

  useTripMapDayFocus({ map, defaultCenter, refs });
  useTripMapFraming({
    map,
    experiences,
    defaultCenter,
    mapState,
    tripId,
    refs,
  });

  const onBoundsChanged = useMemo(
    () =>
      debounce(() => {
        const center = map?.getCenter();

        // No center when the map never finished initializing (e.g. a Maps API
        // key/referer error) — nothing to persist then.
        if (center) {
          setMapState({
            center: { lat: center.lat(), lng: center.lng() },
            zoom: map.getZoom(),
            tripId,
          });
        }
      }, SAVE_CAMERA_DELAY_MS),
    [map, setMapState, tripId],
  );

  useEffect(() => () => onBoundsChanged.cancel?.(), [onBoundsChanged]);

  const onLoad = (mapInstance: any) => {
    setMap(mapInstance);
    if (hasSavedCamera) {
      mapInstance.setCenter(mapState.center);
      if (mapState.zoom) {
        mapInstance.setZoom(mapState.zoom);
        setMapZoom(mapState.zoom);
      }
    } else if (hasValidCenter(defaultCenter)) {
      mapInstance.setCenter(defaultCenter);
      mapInstance.setZoom(COUNTRY_ZOOM);
      // Keep the zoom state in step with the camera onLoad just set.
      setMapZoom(COUNTRY_ZOOM);
    }
  };

  return {
    center: hasSavedCamera ? mapState.center : defaultCenter,
    zoom,
    onLoad,
    onUnmount: () => setMap(null),
    onZoomChanged: (event) => setMapZoom(event.map.getZoom()),
    onBoundsChanged,
  };
}
